import React, { useRef } from 'react';
import {
  ShoppingCart,
  Check,
  Plus
} from 'lucide-react';
import { ABLE_COLORS } from '../../../constants';
import { ShoppingItem } from './index';

/**
 * ShoppingTab - Shopping list display and management
 * 
 * Features:
 * - Sort by name or category
 * - Show/hide bought items
 * - Mark items as bought (circle click)
 * - Inline editing via ShoppingItem
 * - Undo bought items
 */
export default function ShoppingTab({
  // Data
  shoppingList,
  boughtItems,
  categories,
  categoryConfig,
  unitOptions,
  // Rapid entry state
  inputName,
  inputQtyNumber,
  inputQtyUnit,
  inputCategory,
  onInputNameChange,
  onInputQtyNumberChange,
  onInputQtyUnitChange,
  onInputCategoryChange,
  onAddItem,
  onKeyDown,
  // Sort state
  sortBy,
  onSortByChange,
  showBought,
  onShowBoughtToggle,
  // Edit state
  editingItemId,
  editName,
  editQtyNumber,
  editQtyUnit,
  editCategory,
  onEditNameChange,
  onEditQtyNumberChange,
  onEditQtyUnitChange,
  onEditCategoryChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  // Item actions
  onMarkAsBought,
  onRemove,
  onUndoBought,
  onIncrement,
  onDecrement
}) {
  const nameInputRef = useRef(null);

  const handleAddAndFocus = () => {
    onAddItem();
    nameInputRef.current?.focus();
  };

  // Sort shopping list
  const sortedShoppingList = [...shoppingList].sort((a, b) => {
    if (sortBy === 'category') {
      const catA = a.category || 'other';
      const catB = b.category || 'other';
      if (catA !== catB) return catA.localeCompare(catB);
    }
    return a.name.localeCompare(b.name);
  });

  const sortedBoughtItems = [...boughtItems].sort((a, b) => {
    if (sortBy === 'category') {
      const catA = a.category || 'other';
      const catB = b.category || 'other';
      if (catA !== catB) return catA.localeCompare(catB);
    }
    return a.name.localeCompare(b.name);
  });

  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* Rapid Entry Card */}
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        border: `1px solid ${ABLE_COLORS.border}`,
        padding: '16px',
        marginBottom: '16px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Row 1: Item name (full width on mobile) */}
        <div className="shopping-input-row" style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <input
            ref={nameInputRef}
            type="text"
            placeholder="Add to shopping list..."
            value={inputName}
            onChange={(e) => onInputNameChange(e.target.value)}
            onKeyDown={onKeyDown}
            style={{
              flex: 1,
              minWidth: '150px',
              padding: '12px',
              border: `1px solid ${ABLE_COLORS.border}`,
              borderRadius: ABLE_COLORS.radiusMedium,
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          {/* Qty and Unit inline */}
          <div className="shopping-qty-group" style={{ display: 'flex', gap: '6px' }}>
            <input
              type="number"
              placeholder="Qty"
              value={inputQtyNumber}
              onChange={(e) => onInputQtyNumberChange(e.target.value)}
              onKeyDown={onKeyDown}
              min="0"
              step="any"
              style={{
                width: '60px',
                padding: '12px 8px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusMedium,
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <select
              value={inputQtyUnit}
              onChange={(e) => onInputQtyUnitChange(e.target.value)}
              onKeyDown={onKeyDown}
              style={{
                width: '80px',
                padding: '12px 8px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusMedium,
                fontSize: '14px',
                outline: 'none',
                background: ABLE_COLORS.neutral,
                boxSizing: 'border-box',
                color: inputQtyUnit ? ABLE_COLORS.textPrimary : ABLE_COLORS.textMuted
              }}
            >
              {unitOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 2: Category + Add */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            value={inputCategory}
            onChange={(e) => onInputCategoryChange(e.target.value)}
            onKeyDown={onKeyDown}
            style={{
              flex: 1,
              padding: '12px',
              border: `1px solid ${categoryConfig[inputCategory]?.color}40`,
              borderRadius: ABLE_COLORS.radiusMedium,
              fontSize: '13px',
              fontWeight: '600',
              outline: 'none',
              background: `${categoryConfig[inputCategory]?.color}15`,
              boxSizing: 'border-box',
              color: categoryConfig[inputCategory]?.color,
              cursor: 'pointer'
            }}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {categoryConfig[cat]?.label || cat}
              </option>
            ))}
          </select>
          <button
            onClick={handleAddAndFocus}
            style={{
              padding: '12px 14px',
              background: ABLE_COLORS.primary,
              border: 'none',
              borderRadius: ABLE_COLORS.radiusMedium,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Plus size={20} color="white" />
          </button>
        </div>
        <style>{`
          @media (max-width: 400px) {
            .shopping-input-row { flex-direction: column; }
            .shopping-qty-group { width: 100%; justify-content: flex-start; }
          }
        `}</style>
      </div>

      {/* Sort Toggle */}
      {(shoppingList.length > 0 || boughtItems.length > 0) && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <div style={{
            display: 'flex',
            gap: '4px',
            background: ABLE_COLORS.bgSubtle,
            padding: '3px',
            borderRadius: ABLE_COLORS.radiusSmall
          }}>
            <button
              onClick={() => onSortByChange('name')}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: ABLE_COLORS.radiusSmall,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                background: sortBy === 'name' ? ABLE_COLORS.neutral : 'transparent',
                color: sortBy === 'name' ? ABLE_COLORS.textPrimary : ABLE_COLORS.textMuted,
                boxShadow: sortBy === 'name' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
              }}
            >
              Name
            </button>
            <button
              onClick={() => onSortByChange('category')}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: ABLE_COLORS.radiusSmall,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                background: sortBy === 'category' ? ABLE_COLORS.neutral : 'transparent',
                color: sortBy === 'category' ? ABLE_COLORS.textPrimary : ABLE_COLORS.textMuted,
                boxShadow: sortBy === 'category' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
              }}
            >
              Category
            </button>
          </div>
          {boughtItems.length > 0 && (
            <button
              onClick={onShowBoughtToggle}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: ABLE_COLORS.radiusSmall,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                background: showBought ? ABLE_COLORS.primary + '15' : ABLE_COLORS.bgSubtle,
                color: showBought ? ABLE_COLORS.primary : ABLE_COLORS.textMuted
              }}
            >
              {showBought ? 'Hide' : 'Show'} bought ({boughtItems.length})
            </button>
          )}
        </div>
      )}

      {/* Empty State */}
      {shoppingList.length === 0 && boughtItems.length === 0 && !inputName.trim() && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          background: ABLE_COLORS.neutral,
          borderRadius: ABLE_COLORS.cardRadiusLarge,
          border: `1px solid ${ABLE_COLORS.border}`,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <ShoppingCart size={48} color={ABLE_COLORS.textMuted} style={{ marginBottom: '16px' }} />
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: ABLE_COLORS.textPrimary,
            marginBottom: '8px'
          }}>
            Shopping list empty
          </h3>
          <p style={{
            fontSize: '14px',
            color: ABLE_COLORS.textMuted
          }}>
            Add items above or tap the cart icon on pantry items
          </p>
        </div>
      )}

      {/* Active Shopping List */}
      {sortedShoppingList.length > 0 && (
        <div style={{
          background: ABLE_COLORS.neutral,
          borderRadius: ABLE_COLORS.cardRadiusLarge,
          border: `1px solid ${ABLE_COLORS.border}`,
          overflow: 'hidden',
          marginBottom: showBought && boughtItems.length > 0 ? '16px' : 0,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {sortedShoppingList.map((item, idx) => (
            <ShoppingItem
              key={item.id}
              item={item}
              isEditing={editingItemId === item.id}
              editName={editName}
              editQtyNumber={editQtyNumber}
              editQtyUnit={editQtyUnit}
              editCategory={editCategory}
              onEditNameChange={onEditNameChange}
              onEditQtyNumberChange={onEditQtyNumberChange}
              onEditQtyUnitChange={onEditQtyUnitChange}
              onEditCategoryChange={onEditCategoryChange}
              onStartEdit={() => onStartEdit(item)}
              onSaveEdit={() => onSaveEdit(item.id)}
              onCancelEdit={onCancelEdit}
              onMarkAsBought={() => onMarkAsBought(item)}
              onRemove={() => onRemove(item)}
              onIncrement={() => onIncrement(item)}
              onDecrement={() => onDecrement(item)}
              categories={categories}
              categoryConfig={categoryConfig}
              unitOptions={unitOptions}
              isLastItem={idx === sortedShoppingList.length - 1}
            />
          ))}
        </div>
      )}

      {/* Bought Items Section */}
      {showBought && sortedBoughtItems.length > 0 && (
        <div style={{
          background: ABLE_COLORS.neutral,
          borderRadius: ABLE_COLORS.cardRadiusLarge,
          border: `1px solid ${ABLE_COLORS.border}`,
          overflow: 'hidden',
          opacity: 0.7,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{
            padding: '10px 16px',
            background: ABLE_COLORS.bgSubtle,
            borderBottom: `1px solid ${ABLE_COLORS.border}`,
            fontSize: '12px',
            fontWeight: '600',
            color: ABLE_COLORS.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Bought - tap to undo
          </div>
          {sortedBoughtItems.map((item, idx) => {
            const config = categoryConfig[item.category] || categoryConfig.other;
            return (
              <div
                key={item.id}
                onClick={() => onUndoBought(item)}
                style={{
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  borderBottom: idx < sortedBoughtItems.length - 1 ? `1px solid ${ABLE_COLORS.borderLight}` : 'none',
                  transition: 'background 0.15s ease'
                }}
              >
                {/* Filled circle with check */}
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: ABLE_COLORS.primary,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Check size={14} color="white" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '500',
                    color: ABLE_COLORS.textMuted,
                    textDecoration: 'line-through'
                  }}>
                    {item.name}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: ABLE_COLORS.textMuted
                  }}>
                    {config.label}{item.quantity ? ` - ${item.quantity}` : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Helper text */}
      {shoppingList.length > 0 && (
        <p style={{
          textAlign: 'center',
          fontSize: '13px',
          color: ABLE_COLORS.textMuted,
          marginTop: '16px'
        }}>
          Tap circle to mark as bought, tap quantity to edit
        </p>
      )}
    </div>
  );
}
